import { useState, useEffect } from 'react';
import { Button, Card, CardHeader, CardTitle, CardContent } from '../ui';
import api from '../../services/api.service';

interface SubscriptionStatus {
  tier: 'free' | 'premium';
  status: string;
  currentPeriodEnd?: number;
  cancelAtPeriodEnd?: boolean;
  plan?: 'monthly' | 'yearly';
}

export function ManageSubscription() {
  const [subscription, setSubscription] = useState<SubscriptionStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchSubscription();
  }, []);

  const fetchSubscription = async () => {
    try {
      const response = await api.get<SubscriptionStatus>('/subscriptions');
      setSubscription(response.data);
    } catch (err) {
      setError('Failed to load subscription status');
    } finally {
      setLoading(false);
    }
  };

  const handleManageBilling = async () => {
    setActionLoading(true);
    try {
      const response = await api.post<{ url: string }>('/subscriptions/create-portal-session', {});
      if (response.data.url) {
        window.location.href = response.data.url;
      }
    } catch (err) {
      setError('Failed to open billing portal');
    } finally {
      setActionLoading(false);
    }
  };

  const handleCancelSubscription = async () => {
    if (!confirm('Are you sure you want to cancel your subscription? You will keep access until the end of your billing period.')) {
      return;
    }

    setActionLoading(true);
    setError(null);

    try {
      await api.delete('/subscriptions');
      await fetchSubscription();
    } catch (err) {
      setError('Failed to cancel subscription');
    } finally {
      setActionLoading(false);
    }
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="text-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-purple-500 mx-auto"></div>
          <p className="mt-4 text-slate-400">Loading subscription...</p>
        </CardContent>
      </Card>
    );
  }

  const isPremium = subscription?.tier === 'premium';
  const isCancelled = subscription?.cancelAtPeriodEnd;
  const periodEnd = subscription?.currentPeriodEnd
    ? new Date(subscription.currentPeriodEnd * 1000).toLocaleDateString()
    : null;

  return (
    <Card>
      <CardHeader>
        <CardTitle>Subscription</CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        {error && (
          <div className="p-4 bg-red-500/20 border border-red-500 rounded-lg text-red-400">
            {error}
          </div>
        )}

        {/* Current Plan */}
        <div className="flex items-center justify-between p-4 bg-slate-800 rounded-lg">
          <div>
            <h3 className="text-lg font-semibold text-white">
              {isPremium ? '⭐ Premium' : 'Free'} Plan
            </h3>
            {isPremium && subscription?.plan && (
              <p className="text-slate-400 text-sm">
                {subscription.plan === 'yearly' ? 'Yearly' : 'Monthly'} billing
              </p>
            )}
          </div>
          <div
            className={`px-3 py-1 rounded-full text-sm font-medium ${
              isPremium
                ? 'bg-purple-500/20 text-purple-400'
                : 'bg-slate-700 text-slate-400'
            }`}
          >
            {isPremium ? 'Active' : 'Free Tier'}
          </div>
        </div>

        {/* Subscription Details */}
        {isPremium && periodEnd && (
          <div className="p-4 bg-slate-800/50 rounded-lg">
            {isCancelled ? (
              <div>
                <p className="text-yellow-400 font-medium">
                  ⚠️ Subscription cancelled
                </p>
                <p className="text-slate-400 text-sm mt-1">
                  You will have access until {periodEnd}
                </p>
              </div>
            ) : (
              <div>
                <p className="text-slate-400">Next billing date</p>
                <p className="text-white font-medium">{periodEnd}</p>
              </div>
            )}
          </div>
        )}

        {/* Actions */}
        <div className="space-y-3">
          {isPremium ? (
            <>
              <Button
                variant="outline"
                fullWidth
                onClick={handleManageBilling}
                disabled={actionLoading}
              >
                {actionLoading ? 'Loading...' : '💳 Manage Billing'}
              </Button>
              
              {!isCancelled && (
                <Button
                  variant="ghost"
                  fullWidth
                  onClick={handleCancelSubscription}
                  disabled={actionLoading}
                  className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
                >
                  Cancel Subscription
                </Button>
              )}
            </>
          ) : (
            <Button
              variant="primary"
              fullWidth
              onClick={() => (window.location.href = '/subscribe')}
            >
              🚀 Upgrade to Premium
            </Button>
          )}
        </div>

        {/* Premium Benefits */}
        {!isPremium && (
          <div className="mt-6 pt-6 border-t border-slate-700">
            <h4 className="text-white font-medium mb-3">Premium includes:</h4>
            <ul className="space-y-2 text-slate-400 text-sm">
              <li>✅ Access to ALL games</li>
              <li>✅ Global leaderboards</li>
              <li>✅ Custom themes</li>
              <li>✅ Ad-free experience</li>
              <li>✅ Exclusive premium games</li>
            </ul>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
